import { CalendarClock, Hash, Hourglass, Landmark, Zap } from "lucide-react";
import { type FC, type ReactNode, useState } from "react";

import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

import {
  deadlineUrgency,
  formatDeadline,
  relativeDeadline,
  type ImmigrationSummary
} from "./caseHeaderModel";
import { RowLabel } from "./chips/chip";

const Row: FC<{ label: ReactNode; children: ReactNode }> = ({
  label,
  children
}) => (
  <div className="flex min-h-8 items-center gap-1">
    <RowLabel>{label}</RowLabel>
    <span className="flex min-h-7 min-w-0 flex-1 items-center px-1.5 text-xs">
      {children}
    </span>
  </div>
);

const Placeholder: FC<{ children: ReactNode }> = ({ children }) => (
  <span className="text-muted-foreground italic">{children}</span>
);

const DateValue: FC<{ iso: string | null; empty: string }> = ({
  iso,
  empty
}) => {
  const label = formatDeadline(iso);
  if (!label) return <Placeholder>{empty}</Placeholder>;
  const urgency = deadlineUrgency(iso);
  return (
    <span
      className={cn(
        "truncate",
        urgency === "overdue" && "text-danger",
        urgency === "soon" && "text-warn"
      )}
      title={relativeDeadline(iso)}
    >
      {label}
    </span>
  );
};

type Props = {
  immigration: ImmigrationSummary;
  onPremiumChange: (on: boolean) => Promise<void>;
};

/** The filing facts under the case's own properties in the details popover. */
export const ImmigrationRows: FC<Props> = ({
  immigration,
  onPremiumChange
}) => {
  const [saving, setSaving] = useState(false);
  const [first, ...more] = immigration.receipts;

  return (
    <div className="flex flex-col" data-testid="case-immigration">
      <Row
        label={
          <>
            <Hash className="size-3.5 text-fg-3" /> Receipt
          </>
        }
      >
        {first ? (
          <span
            className="truncate font-mono"
            title={immigration.receipts.join(", ")}
          >
            {first}
            {more.length ? (
              <span className="font-sans text-muted-foreground">
                {" "}
                +{more.length}
              </span>
            ) : null}
          </span>
        ) : (
          <Placeholder>None yet</Placeholder>
        )}
      </Row>
      {immigration.serviceCenter ? (
        <Row
          label={
            <>
              <Landmark className="size-3.5 text-fg-3" /> Service Center
            </>
          }
        >
          <span className="truncate">{immigration.serviceCenter}</span>
        </Row>
      ) : null}
      <Row
        label={
          <>
            <CalendarClock className="size-3.5 text-fg-3" /> Priority Date
          </>
        }
      >
        <DateValue iso={immigration.priorityDate} empty="None" />
      </Row>
      <Row
        label={
          <>
            <Hourglass className="size-3.5 text-fg-3" /> Status Expires
          </>
        }
      >
        <DateValue iso={immigration.statusExpiry} empty="Unknown" />
      </Row>
      <Row
        label={
          <>
            <Zap className="size-3.5 text-fg-3" /> Premium
          </>
        }
      >
        <Switch
          aria-label="Premium processing"
          checked={immigration.premiumProcessing}
          disabled={saving}
          onCheckedChange={(on) => {
            setSaving(true);
            onPremiumChange(on)
              .catch(() => undefined)
              .finally(() => setSaving(false));
          }}
        />
      </Row>
    </div>
  );
};
